import React, { useMemo, useState } from "react";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { tr, enUS } from "date-fns/locale";
import { addHours } from "date-fns";
import i18n from 'i18next';
import { handleGraphQLError } from "../utils/graphqlErrorHandler";
import { trLocaleText, enLocaleText } from "../utils/datePickerLocale";
import Slide from "@mui/material/Slide";
import EllipsisCell from "../components/EllipsisCell";
import useTriStateSort from "../hooks/useTriStateSort";
import ClearIcon from "@mui/icons-material/Clear";
import PageHeader from "../components/PageHeader";
import SearchField from "../components/SearchField";
import { formatToLocalTime, formatToLocalDate } from "../utils/dateUtils";
import ResponsiveSelect from "../components/ResponsiveSelect";
import { Grid } from "@mui/material";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    ListItemText,
    MenuItem,
    Paper,
    Snackbar,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    TextField,
    Toolbar,
    Tooltip,
    Typography,
    useMediaQuery
} from "@mui/material";
import displayValue from "../utils/displayValue";
import sortOptionsByLabel from "../utils/sortOptionsByLabel";
import TablePaginationActions from "../components/TablePaginationActions";

import PlaceIcon from "@mui/icons-material/Place";

import { GoogleMap, Marker } from "@react-google-maps/api";
import { gql, useMutation, useQuery } from "@apollo/client";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";

const SlideUp = (props) => <Slide {...props} direction="up"/>;

const FILTER_CUSTOMERS = gql`
    query FilterCustomer {
        filterCustomer(
            criteria: { deleted: false }
            sortBy: { field: "name", direction: ASC }
        ) {
            id
            name
        }
    }
`;


const FILTER_RESELLERS = gql`
    query FilterReseller {
        filterReseller(
            criteria: { deleted: false }
            sortBy: { field: "name", direction: ASC }
        ) {
            id
            name
        }
    }
`;


const FILTER_LOCATIONS = gql`
    query FilterLocationForSelect {
        filterLocation(
            criteria: { deleted: false }
            sortBy: { field: "name", direction: ASC }
        ) {
            id
            name
        }
    }
`;


const DevicePageTemplate = ({ title, subtitle, queries, icon }) => {
    const { t } = useTranslation();
    const isLoaded = true;

    const [mapDialogOpen, setMapDialogOpen] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState(null);

    const role = localStorage.getItem("role") || sessionStorage.getItem("role");
    const canEdit = role !== "CUSTOMER";
    const showResellerColumn = role !== "CUSTOMER" && role !== "RESELLER";
    const showCustomerColumn = role !== "CUSTOMER";
    const showEquipmentColumn = title === "Makmini";
    const totalColumns =
        4 +
        (showEquipmentColumn ? 1 : 0) +
        (showResellerColumn ? 1 : 0) +
        (showCustomerColumn ? 1 : 0);


    const [page, setPage] = useState(0);
    const [rowsPerPage, setRpp] = useState(10);
    const { sortFields, handleSort, getSortProps } = useTriStateSort(
        (updated) => {
            refetch({ ...variables, sortBy: updated });
        }
    );


    const [serialFilter, setSerialFilter] = useState("");
    const [customerFilter, setCustomerFilter] = useState("");
    const [resellerFilter, setResellerFilter] = useState("");
    const [locationFilter, setLocationFilter] = useState("");
    const [equipmentTypesFilter, setEquipmentTypesFilter] = useState("");

    const [debouncedFilters, setDebouncedFilters] = useState({
        serialNumber: "",
        customer: "",
        reseller: "",
        location: "",
        equipmentType: "",
    });


    const [createOpen, setCreateOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);


    const [createForm, setCreateForm] = useState({
        serialNumber: "",
        installationDate: "",
        location: "",
        equipmentTypes: [],
        flowAThreshold: null,
        flowBThreshold: null,
        conductivityAMinThreshold: null,
        conductivityAMaxThreshold: null,
        conductivityBMinThreshold: null,
        conductivityBMaxThreshold: null,
        waterTemperatureAMinThreshold: null,
        waterTemperatureAMaxThreshold: null,
        waterTemperatureBMinThreshold: null,
        waterTemperatureBMaxThreshold: null
    });
    const [editForm, setEditForm] = useState({
        id: "",
        serialNumber: "",
        installationDate: "",
        location: "",
        equipmentTypes: [],
        flowAThreshold: 0,
        flowBThreshold: 0,
        conductivityAMinThreshold: 0,
        conductivityAMaxThreshold: 0,
        conductivityBMinThreshold: 0,
        conductivityBMaxThreshold: 0,
        waterTemperatureAMinThreshold: 0,
        waterTemperatureAMaxThreshold: 0,
        waterTemperatureBMinThreshold: 0,
        waterTemperatureBMaxThreshold: 0
    });
    const [deleteId, setDeleteId] = useState("");


    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const [createError, setCreateError] = useState("");
    const [createApiError, setCreateApiError] = useState({ serialNumber: "", general: "" });
    const [editApiError, setEditApiError] = useState({ serialNumber: "", general: "" });
    const [editError, setEditError] = useState("");
    const [deleteError, setDeleteError] = useState("");

    const isSmall = useMediaQuery("(max-width:600px)");

    const openMapDialog = (locObj) => {
        if (!locObj?.address?.latitude || !locObj?.address?.longitude) return;
        setSelectedLocation(locObj);
        setMapDialogOpen(true);
    };

    const closeMapDialog = () => {
        setMapDialogOpen(false);
        setSelectedLocation(null);
    };


    const variables = useMemo(() => {
        const criteria = {
            deleted: false,
            ...(debouncedFilters.serialNumber ? { serialNumber: debouncedFilters.serialNumber } : {}),
            ...(debouncedFilters.customer ? { customer: debouncedFilters.customer } : {}),
            ...(debouncedFilters.reseller ? { reseller: debouncedFilters.reseller } : {}),
            ...(debouncedFilters.location ? { location: debouncedFilters.location } : {}),
            ...(title === "Makmini" && debouncedFilters.equipmentType
                ? { equipmentType: debouncedFilters.equipmentType }
                : {}),
        };

        return {
            pagination: { page, itemsPerPage: rowsPerPage },
            sortBy: sortFields,
            criteria,
        };
    }, [page, rowsPerPage, sortFields, debouncedFilters]);


    const { data, loading, error, refetch } = useQuery(queries.PAGINATE, {
        variables,
        fetchPolicy: "no-cache",
    });


    const { data: customerData } = useQuery(FILTER_CUSTOMERS, {
        fetchPolicy: "no-cache",
    });
    const { data: resellerData } = useQuery(FILTER_RESELLERS, {
        fetchPolicy: "no-cache",
    });
    const { data: locationData } = useQuery(FILTER_LOCATIONS, {
        fetchPolicy: "no-cache",
    });


    const [createDevice, { loading: creating }] = useMutation(queries.CREATE);
    const [updateDevice, { loading: updating }] = useMutation(queries.UPDATE);
    const [deleteDevice, { loading: deleting }] = useMutation(queries.DELETE);

    const rootKey = Object.keys(data || {})[0];
    const pageInfo = data?.[rootKey]?.pageInfo;
    const rows = data?.[rootKey]?.content ?? [];

    const handleChangePage = (_e, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (e) => {
        setRpp(parseInt(e.target.value, 10));
        setPage(0);
    };


    const handleSearchKeyDown = (e) => {
        if (e.key === "Enter") {
            setDebouncedFilters({
                serialNumber: serialFilter.trim(),
                customer: customerFilter || "",
                reseller: resellerFilter || "",
                location: locationFilter || "",
            });
            setPage(0);
        }
    };

    const applyFilters = () => {
        setDebouncedFilters({
            serialNumber: serialFilter.trim(),
            customer: customerFilter || "",
            reseller: resellerFilter || "",
            location: locationFilter || "",
            equipmentType: equipmentTypesFilter || "",
        });
        setPage(0);
    };


    const openEdit = (row) => {
        const normalizedEquipment = Array.isArray(row.equipmentTypes)
            ? row.equipmentTypes.filter(Boolean)
            : row.equipmentTypes
                ? [row.equipmentTypes].filter(Boolean)
                : [];
        setEditForm({
            id: row.id,
            serialNumber: row.serialNumber || "",
            installationDate: row.installationDate || "",
            location: row.location?.id || "",
            equipmentTypes: normalizedEquipment,
            ...(title === "Makmini" && {
                flowAThreshold: row.flowAThreshold ?? 0,
                flowBThreshold: row.flowBThreshold ?? 0,
                conductivityAMinThreshold: row.conductivityAMinThreshold ?? 0,
                conductivityAMaxThreshold: row.conductivityAMaxThreshold ?? 0,
                conductivityBMinThreshold: row.conductivityBMinThreshold ?? 0,
                conductivityBMaxThreshold: row.conductivityBMaxThreshold ?? 0,
                waterTemperatureAMinThreshold: row.waterTemperatureAMinThreshold ?? 0,
                waterTemperatureAMaxThreshold: row.waterTemperatureAMaxThreshold ?? 0,
                waterTemperatureBMinThreshold: row.waterTemperatureBMinThreshold ?? 0,
                waterTemperatureBMaxThreshold: row.waterTemperatureBMaxThreshold ?? 0,
            }),
        });
        setEditOpen(true);
    };

    const openDelete = (row) => {
        setDeleteId(row.id);
        setEditForm((prev) => ({ ...prev, serialNumber: row.serialNumber }));
        setDeleteOpen(true);
    };

    const handleCloseCreate = () => {
        setCreateOpen(false);
        setCreateError("");
        setCreateApiError({ serialNumber: "", general: "" });
        setCreateForm({
            serialNumber: "", installationDate: "", location: "", equipmentTypes: [],
            flowAThreshold: null,
            flowBThreshold: null,
            conductivityAMinThreshold: null,
            conductivityAMaxThreshold: null,
            conductivityBMinThreshold: null,
            conductivityBMaxThreshold: null,
            waterTemperatureAMinThreshold: null,
            waterTemperatureAMaxThreshold: null,
            waterTemperatureBMinThreshold: null,
            waterTemperatureBMaxThreshold: null,
        });
    };

    const handleCloseEdit = () => {
        setEditOpen(false);
        setEditApiError({ serialNumber: "", general: "" });
        setEditError("");
    };

    const handleCloseDelete = () => {
        setDeleteOpen(false);
        setDeleteError("");
    };

    const doCreate = async () => {
        setCreateApiError({ serialNumber: "", general: "" });
        const normalizedEquipCreate =
            title === "Makmini"
                ? Array.isArray(createForm.equipmentTypes)
                    ? createForm.equipmentTypes.filter(Boolean)
                    : []
                : [];

        if (
            !createForm.serialNumber.trim() ||
            !createForm.installationDate?.toString().trim() ||
            !createForm.location.trim() ||
            (title === "Makmini" && normalizedEquipCreate.length === 0)
        ) {
            setCreateError(t("validation.requiredAll"));
            return;
        }

        const baseInput = {
            serialNumber: createForm.serialNumber.trim(),
            installationDate: createForm.installationDate,
            location: createForm.location,
        };

        const makminiExtra =
            title === "Makmini"
                ? {
                    equipmentTypes: normalizedEquipCreate,
                    flowAThreshold: createForm.flowAThreshold,
                    flowBThreshold: createForm.flowBThreshold,
                    conductivityAMinThreshold: createForm.conductivityAMinThreshold,
                    conductivityAMaxThreshold: createForm.conductivityAMaxThreshold,
                    conductivityBMinThreshold: createForm.conductivityBMinThreshold,
                    conductivityBMaxThreshold: createForm.conductivityBMaxThreshold,
                    waterTemperatureAMinThreshold: createForm.waterTemperatureAMinThreshold,
                    waterTemperatureAMaxThreshold: createForm.waterTemperatureAMaxThreshold,
                    waterTemperatureBMinThreshold: createForm.waterTemperatureBMinThreshold,
                    waterTemperatureBMaxThreshold: createForm.waterTemperatureBMaxThreshold,
                }
                : {};

        const input = { ...baseInput, ...makminiExtra };

        try {
            setCreateError("");
            await createDevice({ variables: { input } });
            handleCloseCreate();
            setSnackbar({ open: true, message: t("devicePage.createSuccess", { title }), severity: "success" });
            refetch();
        } catch (err) {
            handleGraphQLError(err, setCreateApiError, t);
        }
    };

    const doUpdate = async () => {
        setEditApiError({ serialNumber: "", general: "" });
        const { id, ...rest } = editForm;
        const normalizedEquip =
            title === "Makmini"
                ? Array.isArray(rest.equipmentTypes)
                    ? rest.equipmentTypes.filter(Boolean)
                    : rest.equipmentTypes
                        ? [rest.equipmentTypes].filter(Boolean)
                        : []
                : [];


        if (
            !rest.serialNumber.trim() ||
            !rest.installationDate?.toString().trim() ||
            !rest.location.trim() ||
            (title === "Makmini" && normalizedEquip.length === 0)
        ) {
            setEditError(t("validation.requiredAll"));
            return;
        }

        const baseInput = {
            serialNumber: rest.serialNumber.trim(),
            installationDate: rest.installationDate,
            location: rest.location,
        };

        const makminiExtra =
            title === "Makmini"
                ? {
                    equipmentTypes: normalizedEquip,
                    flowAThreshold: rest.flowAThreshold,
                    flowBThreshold: rest.flowBThreshold,
                    conductivityAMinThreshold: rest.conductivityAMinThreshold,
                    conductivityAMaxThreshold: rest.conductivityAMaxThreshold,
                    conductivityBMinThreshold: rest.conductivityBMinThreshold,
                    conductivityBMaxThreshold: rest.conductivityBMaxThreshold,
                    waterTemperatureAMinThreshold: rest.waterTemperatureAMinThreshold,
                    waterTemperatureAMaxThreshold: rest.waterTemperatureAMaxThreshold,
                    waterTemperatureBMinThreshold: rest.waterTemperatureBMinThreshold,
                    waterTemperatureBMaxThreshold: rest.waterTemperatureBMaxThreshold,
                }
                : {};

        const input = { ...baseInput, ...makminiExtra };

        try {
            setEditError("");
            await updateDevice({ variables: { id, input } });
            handleCloseEdit();
            setSnackbar({ open: true, message: t("devicePage.updateSuccess", { title }), severity: "success" });
            refetch();
        } catch (err) {
            handleGraphQLError(err, setEditApiError, t);
        }
    };

    const doDelete = async () => {
        try {
            setDeleteError("");
            await deleteDevice({ variables: { id: deleteId } });
            handleCloseDelete();
            setSnackbar({ open: true, message: t("devicePage.deleteSuccess"), severity: "success" });
            refetch();
        } catch (err) {
            const msg = err?.graphQLErrors?.[0]?.message || t("error.deleteFailed");
            setDeleteError(msg);
        }
    };

    const onResetFilter = () => {
        setSerialFilter("");
        setCustomerFilter("");
        setResellerFilter("");
        setLocationFilter("");
        setDebouncedFilters({
            serialNumber: "",
            customer: "",
            reseller: "",
            location: "",
            equipmentType: "",
        });
        setEquipmentTypesFilter("");
        setPage(0);
    };

    const onRefresh = () => refetch();


    const equipmentTypeOptions = sortOptionsByLabel([
        { value: "", label: t?.("common.all") || "All" },
        {
            value: "ESPRESSO_MACHINE",
            label: t("devicePage.equipmentTypes.ESPRESSO_MACHINE"),
        },
        {
            value: "TEA_MAKER",
            label: t("devicePage.equipmentTypes.TEA_MAKER"),
        },
        {
            value: "ICE_MACHINE",
            label: t("devicePage.equipmentTypes.ICE_MACHINE"),
        },
        {
            value: "TAP",
            label: t("devicePage.equipmentTypes.TAP"),
        },
        {
            value: "OVEN",
            label: t("devicePage.equipmentTypes.OVEN"),
        },
        {
            value: "DISH_WASHER",
            label: t("devicePage.equipmentTypes.DISH_WASHER"),
        },
        {
            value: "WATER_DISPENSER",
            label: t("devicePage.equipmentTypes.WATER_DISPENSER"),
        },
        {
            value: "FILTER_COFFEE_MACHINE",
            label: t("devicePage.equipmentTypes.FILTER_COFFEE_MACHINE"),
        },
        {
            value: "BOILER",
            label: t("devicePage.equipmentTypes.BOILER"),
        },
        {
            value: "STEAMER",
            label: t("devicePage.equipmentTypes.STEAMER"),
        },
        {
            value: "OTHER",
            label: t("devicePage.equipmentTypes.OTHER"),
        },


    ], { preserveFirst: true });

    const sanitizeMultiSelectValue = (value) => {
        if (Array.isArray(value)) return value;
        if (typeof value === "string") return value.split(",").filter(Boolean);
        return [];
    };

    const formatEquipmentValues = (values = []) => {
        const items = Array.isArray(values) ? values : [values];
        return items
            .filter(Boolean)
            .map((key) => equipmentTypeOptions.find((opt) => opt.value === key)?.label || key)
            .join(", ");
    };

    return (
        <Box>
            {!isLoaded ? (
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "60vh",
                    }}
                >
                    <CircularProgress />
                </Box>
            ) : (
                <Box>
                    
                    <PageHeader
                        title={title}
                        subtitle={subtitle}
                        icon={icon}
                    />

                    <Paper elevation={3} sx={{ borderRadius: 2, overflow: "hidden" }}>
                        <Toolbar
                            sx={{
                                px: 2,
                                py: 1,
                                gap: 1,
                                flexWrap: "wrap",
                                borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                                bgcolor: "background.paper",
                            }}
                        >

                            <SearchField
                                label={t("devicePage.fields.serialNumber")}
                                value={serialFilter}
                                onChange={(e) => setSerialFilter(e.target.value)}
                                onKeyDown={handleSearchKeyDown}
                                placeholder={t("devicePage.search.serial")}
                                isSmall={isSmall}
                            />

                            {role !== "CUSTOMER" && role !== "RESELLER" && (

                                <ResponsiveSelect
                                    label={t("devicePage.fields.reseller")}
                                    value={resellerFilter}
                                    onChange={(e) => setResellerFilter(e.target.value)}
                                    onClear={() => setResellerFilter("")}
                                    data={resellerData}
                                    dataKey="filterReseller"
                                    t={t}
                                    isSmall={isSmall}
                                />
                           
                            )}

                            {role !== "CUSTOMER" && (
                                <ResponsiveSelect
                                    label={t("devicePage.fields.customer")}
                                    value={customerFilter}
                                    onChange={(e) => setCustomerFilter(e.target.value)}
                                    onClear={() => setCustomerFilter("")}
                                    data={customerData}
                                    dataKey="filterCustomer"
                                    t={t}
                                    isSmall={isSmall}
                                />

                             
                            )}


                            <ResponsiveSelect
                                label={t("devicePage.fields.location")}
                                value={locationFilter}
                                onChange={(e) => setLocationFilter(e.target.value)}
                                onClear={() => setLocationFilter("")}
                                data={locationData}
                                dataKey="filterLocation"
                                t={t}
                                isSmall={isSmall}
                            />
                          


                            {title === "Makmini" && (

                                <ResponsiveSelect
                                    label={t("devicePage.fields.equipmentType")}
                                    value={equipmentTypesFilter}
                                    onChange={(e) => setEquipmentTypesFilter(e.target.value)}
                                    onClear={() => setEquipmentTypesFilter("")}
                                    options={equipmentTypeOptions}
                                    t={t}
                                />
                             
                            )}

                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<SearchIcon />}
                                onClick={applyFilters}
                                sx={{
                                    height: 40,
                                    px: 2,
                                    textTransform: "none",
                                    fontWeight: 500,
                                    minWidth: isSmall ? "100%" : "auto",
                                }}
                            >
                                {t("common.search")}
                            </Button>


                            <Button
                                variant="outlined"
                                color="secondary"
                                startIcon={<ClearIcon />}
                                onClick={() => {
                                    onResetFilter();

                                }}
                                sx={{
                                    height: 40,
                                    px: 2,
                                    textTransform: "none",
                                    fontWeight: 500,
                                    minWidth: isSmall ? "100%" : "auto",
                                }}
                            >
                                {t("common.clear")}
                            </Button>

                            <Box sx={{ flex: 1 }} />


                            <Tooltip title={t("common.refresh")}>
                                <span>
                                    <IconButton onClick={onRefresh} aria-label="refresh">
                                        <RefreshIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>


                            {role !== "CUSTOMER" && (
                                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
                                    {t("devicePage.createButton")}
                                </Button>
                            )}
                        </Toolbar>

                        {loading ? (
                            <Box sx={{ p: 4, textAlign: "center" }}>
                                <CircularProgress />
                            </Box>
                        ) : error ? (
                            <Box sx={{ p: 2 }}>
                                <Alert severity="error">{error.message}</Alert>
                            </Box>
                        ) : (
                            <>
                                <TableContainer sx={{ maxHeight: 520 }}>
                                    <Table stickyHeader>
                                        <TableHead>
                                            <TableRow>

                                                <EllipsisCell sx={{ bgcolor: "grey.50", fontWeight: 600, fontSize: "0.875rem", borderBottom: "2px solid", borderBottomColor: "divider", color: "text.primary" }}>
                                                    <TableSortLabel
                                                        {...getSortProps("serialNumber")}
                                                        onClick={() => handleSort("serialNumber")}
                                                    >
                                                        {t("devicePage.fields.serialNumber")}
                                                    </TableSortLabel>
                                                </EllipsisCell>


                                                <EllipsisCell sx={{ bgcolor: "grey.50", fontWeight: 600, fontSize: "0.875rem", borderBottom: "2px solid", borderBottomColor: "divider", color: "text.primary" }}>
                                                    <TableSortLabel
                                                        {...getSortProps("installationDate")}
                                                        onClick={() => handleSort("installationDate")}
                                                    >
                                                        {t("devicePage.fields.installationDate")}
                                                    </TableSortLabel>
                                                </EllipsisCell>
                                                {showEquipmentColumn && (
                                                    <EllipsisCell sx={{ bgcolor: "grey.50", fontWeight: 600, fontSize: "0.875rem", borderBottom: "2px solid", borderBottomColor: "divider", color: "text.primary" }}>
                                                        <TableSortLabel
                                                            {...getSortProps("equipmentTypes")}
                                                            onClick={() => handleSort("equipmentTypes")}
                                                        >
                                                            {t("devicePage.fields.equipmentType")}
                                                        </TableSortLabel>
                                                    </EllipsisCell>
                                                )}

                                                {showResellerColumn && (
                                                    <EllipsisCell sx={{ bgcolor: "grey.50", fontWeight: 600, fontSize: "0.875rem", borderBottom: "2px solid", borderBottomColor: "divider", color: "text.primary" }}>
                                                        <TableSortLabel
                                                            {...getSortProps("resellerName")}
                                                            onClick={() => handleSort("resellerName")}
                                                        >
                                                            {t("devicePage.fields.reseller")}
                                                        </TableSortLabel>
                                                    </EllipsisCell>
                                                )}
                                                {showCustomerColumn && (
                                                    <EllipsisCell sx={{ bgcolor: "grey.50", fontWeight: 600, fontSize: "0.875rem", borderBottom: "2px solid", borderBottomColor: "divider", color: "text.primary" }}>
                                                        <TableSortLabel
                                                            {...getSortProps("customerName")}
                                                            onClick={() => handleSort("customerName")}
                                                        >
                                                            {t("devicePage.fields.customer")}
                                                        </TableSortLabel>
                                                    </EllipsisCell>
                                                )}


                                                <EllipsisCell sx={{ bgcolor: "grey.50", fontWeight: 600, fontSize: "0.875rem", borderBottom: "2px solid", borderBottomColor: "divider", color: "text.primary" }}>
                                                    <TableSortLabel
                                                        {...getSortProps("locationName")}

                                                        onClick={() => handleSort("locationName")}
                                                    >
                                                        {t("devicePage.fields.location")}
                                                    </TableSortLabel>
                                                </EllipsisCell>

                                                <EllipsisCell sx={{ bgcolor: "grey.50", fontWeight: 600, fontSize: "0.875rem", borderBottom: "2px solid", borderBottomColor: "divider", color: "text.primary", width: "1%", whiteSpace: "nowrap" }}>
                                                    <TableSortLabel
                                                        {...getSortProps("updatedTimestamp")}
                                                        onClick={() => handleSort("updatedTimestamp")}
                                                    >
                                                        {t("userPage.table.updated")}
                                                    </TableSortLabel>
                                                </EllipsisCell>

                                                {canEdit && (
                                                    <EllipsisCell sx={{ bgcolor: "grey.50", fontWeight: 600, fontSize: "0.875rem", borderBottom: "2px solid", borderBottomColor: "divider", color: "text.primary", width: "1%", whiteSpace: "nowrap" }}>
                                                        {t("userPage.table.actions")}
                                                    </EllipsisCell>
                                                )}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {rows.length === 0 ? (
                                                <TableRow>
                                                    <EllipsisCell colSpan={totalColumns} align="center">
                                                        {t("userPage.noRecords")}
                                                    </EllipsisCell>
                                                </TableRow>
                                            ) : (
                                                rows.map((r) => (
                                                    <TableRow
                                                        key={r.id}
                                                        hover
                                                        sx={{
                                                            "&:nth-of-type(even)": {
                                                                bgcolor: "grey.50"
                                                            },
                                                            "&:hover": {
                                                                bgcolor: "action.hover"
                                                            }
                                                        }}
                                                    >
                                                        <EllipsisCell value={displayValue(r.serialNumber)} />

                                                        <EllipsisCell value={formatToLocalDate(r.installationDate)} />
                                                        {showEquipmentColumn && (
                                                            <EllipsisCell>
                                                                {Array.isArray(r.equipmentTypes) && r.equipmentTypes.length
                                                                    ? formatEquipmentValues(r.equipmentTypes)
                                                                    : "-"}
                                                            </EllipsisCell>
                                                        )}
                                                        {showResellerColumn && (
                                                            <EllipsisCell value={displayValue(r.location?.customer?.reseller?.name)} />

                                                        )}
                                                        {showCustomerColumn && (
                                                            <EllipsisCell value={displayValue(r.location?.customer?.name)} />

                                                        )}

                                                        <EllipsisCell value={displayValue(r.location?.name)} />

                                                        <EllipsisCell sx={{ whiteSpace: "nowrap" }}>
                                                            {r.updatedTimestamp
                                                                ? formatToLocalTime(r.updatedTimestamp)
                                                                : "-"}
                                                        </EllipsisCell>
                                                        {canEdit && (
                                                            <EllipsisCell sx={{ whiteSpace: "nowrap" }}>
                                                                {r.location?.address?.latitude && r.location?.address?.longitude && (
                                                                    <Tooltip title={t("devicePage.map.showOnMap")}>
                                                                        <IconButton
                                                                            size="small"
                                                                            color="primary"
                                                                            onClick={() => openMapDialog(r.location)}
                                                                            sx={{ p: 0.5 }}
                                                                        >
                                                                            <PlaceIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                )}
                                                                <Tooltip title={t("common.update")}>
                                                                    <IconButton size="small" onClick={() => openEdit(r)}>
                                                                        <EditIcon />
                                                                    </IconButton>
                                                                </Tooltip>
                                                                <Tooltip title={t("common.delete")}>
                                                                    <IconButton size="small" color="error" onClick={() => openDelete(r)}>
                                                                        <DeleteIcon />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </EllipsisCell>
                                                        )}

                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                <TablePagination
                                    labelRowsPerPage={t("common.rowsPerPage")}
                                    component="div"
                                    rowsPerPageOptions={[10, 25, 50, 100]}
                                    count={pageInfo?.totalElements ?? 0}
                                    rowsPerPage={pageInfo?.pageSize ?? rowsPerPage}
                                    page={pageInfo?.pageNumber ?? page}
                                    onPageChange={handleChangePage}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                    ActionsComponent={TablePaginationActions}
                                />
                            </>
                        )}
                    </Paper>


                    <Dialog open={createOpen} onClose={handleCloseCreate} fullWidth maxWidth="sm">
                        <DialogTitle sx={{ fontWeight: "bold" }}>{t("devicePage.createTitle")}</DialogTitle>
                        <Divider sx={{ mb: 1, borderColor: "divider", opacity: 0.7 }} />
                        {createApiError.general && <Alert severity="error" sx={{ mb: 2 }}>{createApiError.general}</Alert>}
                        <DialogContent sx={{ pt: 1 }}>
                            <Stack spacing={2} sx={{ mt: 1 }}>
                                <TextField
                                    label={t("devicePage.fields.serialNumber")}
                                    value={createForm.serialNumber}
                                    onChange={(e) => setCreateForm({ ...createForm, serialNumber: e.target.value })}
                                    error={!!createApiError.serialNumber || (createError && !createForm.serialNumber.trim())}
                                    helperText={
                                        createApiError.serialNumber ||
                                        (createError && !createForm.serialNumber.trim() ? t("validation.requiredField") : "")
                                    }
                                    autoFocus
                                />

                                <LocalizationProvider
                                    dateAdapter={AdapterDateFns}
                                    adapterLocale={i18n.language.startsWith("tr") ? tr : enUS}
                                    localeText={i18n.language.startsWith("tr") ? trLocaleText : enLocaleText}
                                >

                                    <DatePicker
                                        label={t("devicePage.fields.installationDate")}
                                        value={createForm.installationDate ? new Date(createForm.installationDate).setHours(3, 0, 0, 0) : null}
                                        onChange={(newDate) => setCreateForm({ ...createForm, installationDate: newDate })}

                                        slotProps={{
                                            textField: {
                                                error: createError && !createForm.installationDate?.toString().trim(),
                                                helperText: createError && !createForm.installationDate?.toString().trim() ? t("validation.requiredField") : ""
                                            },
                                        }}
                                    />

                                </LocalizationProvider>

                                <ResponsiveSelect
                                    label={t("devicePage.fields.location")}
                                    value={createForm.location}
                                    onChange={(e) => setCreateForm({ ...createForm, location: e.target.value })}
                                    data={locationData}
                                    dataKey="filterLocation"
                                    fullWidth={true}
                                    error={createError && !createForm.location.trim()}
                                    helperText={
                                        createError && !createForm.location.trim() ? t("validation.requiredField") : ""
                                    }
                                />

                                {title === "Makmini" && (
                                    <TextField
                                        select
                                        fullWidth
                                        label={t("devicePage.fields.equipmentType")}
                                        value={createForm.equipmentTypes}
                                        onChange={(e) =>
                                            setCreateForm({
                                                ...createForm,
                                                equipmentTypes: sanitizeMultiSelectValue(e.target.value),
                                            })
                                        }
                                        SelectProps={{
                                            multiple: true,
                                            renderValue: (selected) => formatEquipmentValues(selected),
                                        }}
                                        error={!createForm.equipmentTypes.length && !!createError}
                                        helperText={
                                            !createForm.equipmentTypes.length && createError
                                                ? t("validation.requiredField")
                                                : ""
                                        }
                                    >
                                        {equipmentTypeOptions
                                            .filter((opt) => opt.value)
                                            .map((opt) => (
                                                <MenuItem key={opt.value} value={opt.value}>
                                                    <Checkbox
                                                        checked={createForm.equipmentTypes.indexOf(opt.value) > -1}
                                                    />
                                                    <ListItemText primary={opt.label} />
                                                </MenuItem>
                                            ))}
                                    </TextField>


                                )}
                                {title === "Makmini" && (
                                    <>
                                        <Box
                                            sx={{
                                                width: "100%",
                                                display: "grid",
                                                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                                                gap: 2,
                                            }}
                                        >
                                            <TextField
                                                fullWidth
                                                label={t("devicePageMakmini.columns.flowAThreshold")}
                                                type="number"
                                                value={createForm.flowAThreshold}
                                                onChange={(e) =>
                                                    setCreateForm({
                                                        ...createForm,
                                                        flowAThreshold: Number(e.target.value),
                                                    })
                                                }
                                            />
                                            <TextField
                                                fullWidth
                                                label={t("devicePageMakmini.columns.flowBThreshold")}
                                                type="number"
                                                value={createForm.flowBThreshold}
                                                onChange={(e) =>
                                                    setCreateForm({
                                                        ...createForm,
                                                        flowBThreshold: Number(e.target.value),
                                                    })
                                                }
                                            />
                                            <TextField fullWidth
                                                label={t("devicePageMakmini.columns.conductivityAMinThreshold")}
                                                type="number"
                                                value={createForm.conductivityAMinThreshold}
                                                onChange={(e) =>
                                                    setCreateForm({
                                                        ...createForm,
                                                        conductivityAMinThreshold: Number(e.target.value),
                                                    })
                                                }
                                            />
                                            <TextField
                                                label={t("devicePageMakmini.columns.conductivityAMaxThreshold")}
                                                type="number"
                                                value={createForm.conductivityAMaxThreshold}
                                                onChange={(e) =>
                                                    setCreateForm({
                                                        ...createForm,
                                                        conductivityAMaxThreshold: Number(e.target.value),
                                                    })
                                                }
                                            />
                                            <TextField fullWidth
                                                label={t("devicePageMakmini.columns.conductivityBMinThreshold")}
                                                type="number"
                                                value={createForm.conductivityBMinThreshold}
                                                onChange={(e) =>
                                                    setCreateForm({
                                                        ...createForm,
                                                        conductivityBMinThreshold: Number(e.target.value),
                                                    })
                                                }
                                            />
                                            <TextField
                                                fullWidth
                                                label={t("devicePageMakmini.columns.conductivityBMaxThreshold")}
                                                type="number"
                                                value={createForm.conductivityBMaxThreshold}
                                                onChange={(e) =>
                                                    setCreateForm({
                                                        ...createForm,
                                                        conductivityBMaxThreshold: Number(e.target.value),
                                                    })
                                                }
                                            />
                                            <TextField
                                                fullWidth
                                                label={t("devicePageMakmini.columns.waterTemperatureAMinThreshold")}
                                                type="number"
                                                value={createForm.waterTemperatureAMinThreshold}
                                                onChange={(e) =>
                                                    setCreateForm({
                                                        ...createForm,
                                                        waterTemperatureAMinThreshold: Number(e.target.value),
                                                    })
                                                }
                                            />
                                            <TextField
                                                fullWidth
                                                label={t("devicePageMakmini.columns.waterTemperatureAMaxThreshold")}
                                                type="number"
                                                value={createForm.waterTemperatureAMaxThreshold}
                                                onChange={(e) =>
                                                    setCreateForm({
                                                        ...createForm,
                                                        waterTemperatureAMaxThreshold: Number(e.target.value),
                                                    })
                                                }
                                            />
                                            <TextField
                                                fullWidth
                                                label={t("devicePageMakmini.columns.waterTemperatureBMinThreshold")}
                                                type="number"
                                                value={createForm.waterTemperatureBMinThreshold}
                                                onChange={(e) =>
                                                    setCreateForm({
                                                        ...createForm,
                                                        waterTemperatureBMinThreshold: Number(e.target.value),
                                                    })
                                                }
                                            />
                                            <TextField
                                                fullWidth
                                                label={t("devicePageMakmini.columns.waterTemperatureBMaxThreshold")}
                                                type="number"
                                                value={createForm.waterTemperatureBMaxThreshold}
                                                onChange={(e) =>
                                                    setCreateForm({
                                                        ...createForm,
                                                        waterTemperatureBMaxThreshold: Number(e.target.value),
                                                    })
                                                }
                                            />

                                        </Box>
                                    </>
                                )}
                            </Stack>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCloseCreate}>{t("common.cancel")}</Button>
                            <Button onClick={doCreate} disabled={creating} aria-busy={creating} variant="contained">
                                {creating ? <CircularProgress size={20} /> : t("common.create")}
                            </Button>
                        </DialogActions>
                    </Dialog>


                    <Dialog open={editOpen} onClose={handleCloseEdit} fullWidth maxWidth="sm">
                        <DialogTitle sx={{ fontWeight: "bold" }}>{t("devicePage.updateTitle")}</DialogTitle>
                        <Divider sx={{ mb: 1, borderColor: "divider", opacity: 0.7 }} />
                        {editApiError.general && <Alert severity="error" sx={{ mb: 2 }}>{editApiError.general}</Alert>}
                        <DialogContent sx={{ pt: 1 }}>
                            <Stack spacing={2} sx={{ mt: 1 }}>
                                <TextField
                                    label={t("devicePage.fields.serialNumber")}
                                    value={editForm.serialNumber}
                                    onChange={(e) => setEditForm({ ...editForm, serialNumber: e.target.value })}
                                    error={!!editApiError.serialNumber || (editError && !editForm.serialNumber.trim())}
                                    helperText={
                                        editApiError.serialNumber ||
                                        (editError && !editForm.serialNumber.trim() ? t("validation.requiredField") : "")
                                    }
                                    autoFocus
                                />
                                <LocalizationProvider
                                    dateAdapter={AdapterDateFns}
                                    adapterLocale={i18n.language.startsWith("tr") ? tr : enUS}
                                    localeText={i18n.language.startsWith("tr") ? trLocaleText : enLocaleText}
                                >
                                    <DatePicker
                                        label={t("devicePage.fields.installationDate")}
                                        value={editForm.installationDate ? new Date(editForm.installationDate).setHours(3, 0, 0, 0) : null}
                                        onChange={(newDate) => setEditForm({ ...editForm, installationDate: newDate })}

                                        slotProps={{
                                            textField: {
                                                error: editError && !editForm.installationDate?.toString().trim(),
                                                helperText: editError && !editForm.installationDate?.toString().trim() ? t("validation.requiredField") : ""
                                            },
                                        }}
                                    />
                                </LocalizationProvider>


                                <ResponsiveSelect
                                    label={t("devicePage.fields.location")}
                                    value={editForm.location}
                                    onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                                    data={locationData}
                                    dataKey="filterLocation"
                                    fullWidth={true}
                                    error={editError && !editForm.location.trim()}
                                    helperText={
                                        editError && !editForm.location.trim() ? t("validation.requiredField") : ""
                                    }
                                />



                                {title === "Makmini" && (
                                    <TextField
                                        select
                                        fullWidth
                                        label={t("devicePage.fields.equipmentType")}
                                        value={editForm.equipmentTypes}
                                        onChange={(e) =>
                                            setEditForm({
                                                ...editForm,
                                                equipmentTypes: sanitizeMultiSelectValue(e.target.value),
                                            })
                                        }
                                        SelectProps={{
                                            multiple: true,
                                            renderValue: (selected) => formatEquipmentValues(selected),
                                        }}
                                        error={!editForm.equipmentTypes.length && !!editError}
                                        helperText={
                                            !editForm.equipmentTypes.length && editError
                                                ? t("validation.requiredField")
                                                : ""
                                        }
                                    >
                                        {equipmentTypeOptions
                                            .filter((opt) => opt.value)
                                            .map((opt) => (
                                                <MenuItem key={opt.value} value={opt.value}>
                                                    <Checkbox checked={editForm.equipmentTypes.indexOf(opt.value) > -1} />
                                                    <ListItemText primary={opt.label} />
                                                </MenuItem>
                                            ))}
                                    </TextField>
                                )}

                                {title === "Makmini" && (
                                    <>
                                     <Box
                                            sx={{
                                                width: "100%",
                                                display: "grid",
                                                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                                                gap: 2,
                                            }}
                                        >
                                        <TextField
                                        fullWidth
                                            label={t("devicePageMakmini.columns.flowAThreshold")}
                                            type="number"
                                            value={editForm.flowAThreshold}
                                            onChange={(e) =>
                                                setEditForm({
                                                    ...editForm,
                                                    flowAThreshold: Number(e.target.value),
                                                })
                                            }
                                        />
                                        <TextField
                                        fullWidth
                                            label={t("devicePageMakmini.columns.flowBThreshold")}
                                            type="number"
                                            value={editForm.flowBThreshold}
                                            onChange={(e) =>
                                                setEditForm({
                                                    ...editForm,
                                                    flowBThreshold: Number(e.target.value),
                                                })
                                            }
                                        />

                                        <TextField
                                        fullWidth
                                            label={t("devicePageMakmini.columns.conductivityAMinThreshold")}
                                            type="number"
                                            value={editForm.conductivityAMinThreshold}
                                            onChange={(e) =>
                                                setEditForm({
                                                    ...editForm,
                                                    conductivityAMinThreshold: Number(e.target.value),
                                                })
                                            }
                                        />
                                        <TextField
                                        fullWidth
                                            label={t("devicePageMakmini.columns.conductivityAMaxThreshold")}
                                            type="number"
                                            value={editForm.conductivityAMaxThreshold}
                                            onChange={(e) =>
                                                setEditForm({
                                                    ...editForm,
                                                    conductivityAMaxThreshold: Number(e.target.value),
                                                })
                                            }
                                        />
                                        <TextField
                                        fullWidth
                                            label={t("devicePageMakmini.columns.conductivityBMinThreshold")}
                                            type="number"
                                            value={editForm.conductivityBMinThreshold}
                                            onChange={(e) =>
                                                setEditForm({
                                                    ...editForm,
                                                    conductivityBMinThreshold: Number(e.target.value),
                                                })
                                            }
                                        />
                                        <TextField
                                        fullWidth
                                            label={t("devicePageMakmini.columns.conductivityBMaxThreshold")}
                                            type="number"
                                            value={editForm.conductivityBMaxThreshold}
                                            onChange={(e) =>
                                                setEditForm({
                                                    ...editForm,
                                                    conductivityBMaxThreshold: Number(e.target.value),
                                                })
                                            }
                                        />

                                        <TextField
                                        fullWidth
                                            label={t("devicePageMakmini.columns.waterTemperatureAMinThreshold")}
                                            type="number"
                                            value={editForm.waterTemperatureAMinThreshold}
                                            onChange={(e) =>
                                                setEditForm({
                                                    ...editForm,
                                                    waterTemperatureAMinThreshold: Number(e.target.value),
                                                })
                                            }
                                        />
                                        <TextField
                                        fullWidth
                                            label={t("devicePageMakmini.columns.waterTemperatureAMaxThreshold")}
                                            type="number"
                                            value={editForm.waterTemperatureAMaxThreshold}
                                            onChange={(e) =>
                                                setEditForm({
                                                    ...editForm,
                                                    waterTemperatureAMaxThreshold: Number(e.target.value),
                                                })
                                            }
                                        />
                                        <TextField
                                        fullWidth
                                            label={t("devicePageMakmini.columns.waterTemperatureBMinThreshold")}
                                            type="number"
                                            value={editForm.waterTemperatureBMinThreshold}
                                            onChange={(e) =>
                                                setEditForm({
                                                    ...editForm,
                                                    waterTemperatureBMinThreshold: Number(e.target.value),
                                                })
                                            }
                                        />
                                        <TextField
                                        fullWidth
                                            label={t("devicePageMakmini.columns.waterTemperatureBMaxThreshold")}
                                            type="number"
                                            value={editForm.waterTemperatureBMaxThreshold}
                                            onChange={(e) =>
                                                setEditForm({
                                                    ...editForm,
                                                    waterTemperatureBMaxThreshold: Number(e.target.value),
                                                })
                                            }
                                        />
                                        </Box>
                                    </>
                                )}
                            </Stack>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCloseEdit}>{t("common.cancel")}</Button>
                            <Button onClick={doUpdate} disabled={updating} aria-busy={updating} variant="contained">
                                {updating ? <CircularProgress size={20} /> : t("common.update")}
                            </Button>
                        </DialogActions>
                    </Dialog>


                    <Dialog open={deleteOpen} onClose={handleCloseDelete}>
                        <DialogTitle sx={{ fontWeight: "bold" }}>{t("devicePage.deleteConfirmTitle")}</DialogTitle>
                        <Divider sx={{ mb: 1, borderColor: "divider", opacity: 0.7 }} />
                        {deleteError && <Alert severity="error" sx={{ mb: 2 }}>{deleteError}</Alert>}
                        <DialogContent>
                            <Typography variant="h6" sx={{ color: "#d32f2f", fontWeight: "bold", mt: 1 }}>
                                {editForm.serialNumber}
                            </Typography>
                            <Typography>{t("devicePage.deleteConfirmMessage")}</Typography>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCloseDelete}>{t("common.cancel")}</Button>
                            <Button
                                onClick={doDelete}
                                color="error"
                                disabled={deleting}
                                aria-busy={deleting}
                                variant="contained"
                            >
                                {deleting ? <CircularProgress size={20} /> : t("common.delete")}
                            </Button>
                        </DialogActions>
                    </Dialog>


                    <Dialog
                        open={mapDialogOpen}
                        onClose={closeMapDialog}
                        fullWidth
                        maxWidth="sm"
                        PaperProps={{ sx: { borderRadius: 2 } }}
                    >
                        <DialogTitle sx={{ fontWeight: "bold" }}>{selectedLocation?.name || t("devicePage.map.title")} — {t("devicePage.map.header")}</DialogTitle>
                        <Divider sx={{ mb: 1, borderColor: "divider", opacity: 0.7 }} />
                        <DialogContent sx={{ height: 400, p: 0 }}>
                            {selectedLocation?.address?.latitude && selectedLocation?.address?.longitude ? (
                                <GoogleMap
                                    mapContainerStyle={{ width: "100%", height: "100%" }}
                                    center={{
                                        lat: selectedLocation.address.latitude,
                                        lng: selectedLocation.address.longitude,
                                    }}
                                    zoom={16}
                                    options={{
                                        disableDefaultUI: true,
                                        gestureHandling: "cooperative",
                                    }}
                                >
                                    <Marker
                                        position={{
                                            lat: selectedLocation.address.latitude,
                                            lng: selectedLocation.address.longitude,
                                        }}
                                    />
                                </GoogleMap>
                            ) : (
                                <Box
                                    sx={{
                                        height: "100%",
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                    }}
                                >
                                    <Typography>{t("devicePage.map.noCoordinates")}</Typography>
                                </Box>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={closeMapDialog}>{t("common.close")}</Button>
                        </DialogActions>
                    </Dialog>

                    <Snackbar
                        TransitionComponent={SlideUp}
                        open={snackbar.open}
                        autoHideDuration={2500}
                        onClose={() => setSnackbar({ ...snackbar, open: false })}
                        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                        sx={{
                            "& .MuiAlert-root": {
                                borderRadius: 2,
                                boxShadow: "0px 4px 12px rgba(0,0,0,0.25)",
                                fontWeight: 500,
                                minWidth: 260,
                                justifyContent: "center",
                            },
                        }}
                    >
                        <Alert
                            severity={snackbar.severity}
                            onClose={() => setSnackbar({ ...snackbar, open: false })}
                            variant="filled"
                        >
                            {snackbar.message}
                        </Alert>
                    </Snackbar>
                </Box>
            )}
        </Box>
    );
};

export default DevicePageTemplate;
